import {
  Badge,
  Button,
  type ButtonProps,
  Drawer,
  type DrawerProps,
  Flex,
  Form,
} from 'antd';
import intersection from 'lodash/intersection';
import keys from 'lodash/keys';
import React, { useEffect, useState } from 'react';

import { IconClose, IconFilter } from '@assets';

import FilterConstructorControls, {
  type FilterControl,
} from './FilterConstructorControls';
import * as S from './styled';

type FilterValues = Record<string, unknown>;

type FilterConstructorDrawerProps = {
  initialParams?: FilterValues;
  controls: FilterControl[];
  buttonParams?: ButtonProps;
  onSubmit?: (values: FilterValues | string[] | null) => void;
} & DrawerProps;

const FilterConstructorDrawer: React.FC<FilterConstructorDrawerProps> = ({
  initialParams,
  controls,
  buttonParams,
  onSubmit,
  ...rest
}) => {
  const [form] = Form.useForm();

  const [isOpen, setIsOpen] = useState(false);
  const [formValues, setFormValues] = useState<FilterValues | null>(null);

  useEffect(
    () => {
      const names = getControlsName();

      form.setFieldsValue(initialParams);

      if (intersection(names, keys(initialParams)).length) {
        setFormValues(initialParams || null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getControlsName = () =>
    controls.map((control) => control.formName || '');

  const handleSubmit = (values: FilterValues) => {
    setIsOpen(false);

    onSubmit?.(values);
    setFormValues(values);
  };

  const handleReset = () => {
    form.resetFields();

    onSubmit?.({
      IS_RESET: true,
    });
    setFormValues(null);
    setIsOpen(false);
  };

  return (
    <>
      <Drawer
        width={451}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        closable={false}
        {...rest}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          requiredMark={false}
        >
          <S.DrawerContainer>
            <Flex gap={24} vertical>
              <Flex justify="space-between" align="center">
                <S.DrawerTitle>Filter</S.DrawerTitle>

                <Button
                  type="text"
                  icon={<IconClose />}
                  size="small"
                  onClick={() => setIsOpen(false)}
                />
              </Flex>

              <Flex gap={24} vertical>
                {controls.map((control) => (
                  <FilterConstructorControls
                    key={`${control.type}-${control?.formName}`}
                    {...control}
                  />
                ))}
              </Flex>
            </Flex>
          </S.DrawerContainer>

          <Flex gap={10}>
            <Button type="default" size="large" onClick={handleReset} block>
              Reset all
            </Button>

            <Button type="primary" size="large" htmlType="submit" block>
              Apply
            </Button>
          </Flex>
        </Form>
      </Drawer>

      <Badge dot={!!formValues}>
        <Button
          icon={<IconFilter width={24} />}
          type="secondary"
          onClick={() => setIsOpen(true)}
          size="large"
          {...buttonParams}
        />
      </Badge>
    </>
  );
};

export default FilterConstructorDrawer;
